import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Flame, Sparkles, BookOpen, Map as MapIcon, ScrollText } from "lucide-react";
import { useSeoTitle } from "@/lib/useSeoTitle";
import { CONTENT_FIRST_PUBLISHED, CONTENT_LAST_UPDATED } from "@/data/models";

/**
 * SeoLandingLayout — shared shell for the 6 high-intent SEO pages under
 * /ai-novel-game, /ai-dnd-game, /ai-dungeon-alternative, etc.
 *
 * Each concrete page passes its own copy blocks in — the layout only
 * handles Nav, hero, CTA row, features grid, "Explore" internal links,
 * FAQ section, and SEO title/meta. Kept intentionally small so the six
 * pages stay maintainable and don't drift into near-duplicates.
 *
 * All content is real, visible, and rendered client-side after React
 * hydrates. The homepage carries a separate raw-HTML fallback for
 * bots that don't execute JS; these SEO pages rely on modern crawlers
 * (Googlebot, Bingbot, GPTBot, ClaudeBot, PerplexityBot) all of which
 * execute JS by 2026.
 */
export default function SeoLandingLayout({
  path,          // "/ai-novel-game"
  title,         // full <title> tag
  description,   // meta description
  eyebrow,       // small uppercase label above H1
  h1,            // primary H1
  h1Accent,      // optional italic accent phrase to close the H1
  lede,          // one-paragraph description under the H1
  body,          // JSX — free-form middle section
  features,      // [{ icon, title, body }]
  faq,           // [{ q, a }]
  comparison,    // optional { competitor, rows: [{ feature, us, them }] }
  testId,        // data-testid for the outer <div>
}) {
  // Every SEO landing page carries a two-step breadcrumb (Home → this page)
  // so search engines can render the URL trail below the title in SERPs and
  // LLMs can locate the page inside the sitemap graph.
  const breadcrumbs = React.useMemo(
    () => [
      { name: "Home", path: "/" },
      { name: eyebrow || h1 || "Page", path },
    ],
    [eyebrow, h1, path]
  );

  useSeoTitle({ title, description, path, breadcrumbs });

  // Inject FAQPage JSON-LD once the component mounts. React strict-mode
  // safety: the effect scopes the <script> to this page's path so it's
  // torn down when we navigate away and rebuilt on re-mount. Text is the
  // EXACT visible copy so Google's rich-result check sees consistent Q/A.
  React.useEffect(() => {
    if (!Array.isArray(faq) || faq.length === 0) return () => {};
    const payload = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-faq-path", path);
    el.textContent = JSON.stringify(payload);
    document.head.appendChild(el);
    return () => {
      // Idempotent cleanup — remove all FAQ scripts we own for this path
      // so a route change doesn't leak stale schema into the head.
      document.head
        .querySelectorAll(`script[data-seo-faq-path="${path}"]`)
        .forEach((n) => n.remove());
    };
  }, [faq, path]);

  return (
    <div data-testid={testId} className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <main id="main-content" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        {/* Hero */}
        <header className="text-center mb-14">
          {eyebrow && (
            <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-amber-400/80 mb-4">
              {eyebrow}
            </p>
          )}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight">
            {h1}
            {h1Accent && (
              <>
                <br />
                <span className="gold-text italic">{h1Accent}</span>
              </>
            )}
          </h1>
          {lede && (
            <p className="font-body text-amber-100/75 mt-5 max-w-2xl mx-auto leading-relaxed text-base sm:text-lg">
              {lede}
            </p>
          )}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/trial">
              <Button
                data-testid="seo-trial-btn"
                className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui px-6 h-12"
              >
                <Flame className="w-4 h-4 mr-2" /> Try 5 turns — no signup
              </Button>
            </Link>
            <Link to="/register">
              <Button
                data-testid="seo-register-btn"
                variant="outline"
                className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 font-ui px-6 h-12 bg-transparent"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Create free account
              </Button>
            </Link>
          </div>
        </header>

        {/* Body — page-specific narrative copy */}
        {body && (
          <section className="mb-14 font-body text-amber-100/85 leading-relaxed space-y-4 text-base sm:text-lg max-w-3xl mx-auto">
            {body}
          </section>
        )}

        {/* Features */}
        {Array.isArray(features) && features.length > 0 && (
          <section className="mb-14">
            <h2 className="font-display text-2xl sm:text-3xl text-amber-50 mb-6 text-center">
              What you get
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="parchment gold-border rounded-xl p-5"
                >
                  <f.icon className="w-6 h-6 text-amber-400 mb-3" />
                  <h3 className="font-display text-lg text-amber-100 mb-2">{f.title}</h3>
                  <p className="font-body text-sm text-amber-100/75 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Explore — internal links required on every SEO page */}
        <section className="mb-14">
          <h2 className="font-display text-2xl sm:text-3xl text-amber-50 mb-6 text-center">
            Explore TalesTalesTales
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { to: "/trial",       icon: Flame,      label: "Free 5-turn trial" },
              { to: "/register",    icon: Sparkles,   label: "Register free" },
              { to: "/worlds",      icon: MapIcon,    label: "Browse worlds" },
              { to: "/how-to-play", icon: BookOpen,   label: "How to play" },
              { to: "/pricing",     icon: ScrollText, label: "Pricing" },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`seo-nav-${c.to.replace(/\//g, "")}`}
                className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200 hover:text-amber-100 flex items-center gap-2"
              >
                <c.icon className="w-3.5 h-3.5" /> {c.label}
              </Link>
            ))}
          </div>
        </section>

        {/* Optional comparison table — factual, neutral wording. Renders
            between "Explore" and the FAQ block on /novelai-alternative
            and /ai-dungeon-alternative. */}
        {comparison && Array.isArray(comparison.rows) && comparison.rows.length > 0 && (
          <section className="mb-14 max-w-3xl mx-auto" data-testid="seo-comparison">
            <h2 className="font-display text-2xl sm:text-3xl text-amber-50 mb-6 text-center">
              Feature comparison
            </h2>
            <div className="parchment gold-border rounded-xl overflow-hidden">
              <table className="w-full text-sm font-body">
                <thead className="bg-stone-950/60 font-ui text-[10px] uppercase tracking-widest text-amber-300/80">
                  <tr>
                    <th className="text-left px-4 py-3">Feature</th>
                    <th className="text-left px-4 py-3">TalesTalesTales</th>
                    <th className="text-left px-4 py-3">{comparison.competitor}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-700/15">
                  {comparison.rows.map((row, i) => (
                    <tr key={i} data-testid={`seo-comparison-row-${i}`} className="hover:bg-amber-900/10">
                      <td className="px-4 py-3 text-amber-100/90 font-ui text-xs uppercase tracking-widest">
                        {row.feature}
                      </td>
                      <td className="px-4 py-3 text-amber-100/90">{row.us}</td>
                      <td className="px-4 py-3 text-amber-100/70">{row.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-ui text-[10px] uppercase tracking-widest text-amber-300/40 mt-3 text-center">
              Details of third-party products change frequently — consult their site for the latest.
            </p>
          </section>
        )}

        {/* FAQ */}
        {Array.isArray(faq) && faq.length > 0 && (
          <section className="mb-8 max-w-3xl mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl text-amber-50 mb-6 text-center">
              Frequently asked
            </h2>
            <div className="space-y-3">
              {faq.map((item, i) => (
                <details
                  key={i}
                  className="parchment gold-border rounded-lg p-4 group"
                  data-testid={`seo-faq-${i}`}
                >
                  <summary className="font-ui text-sm uppercase tracking-widest text-amber-200 cursor-pointer">
                    {item.q}
                  </summary>
                  <p className="mt-3 font-body text-amber-100/80 leading-relaxed text-sm sm:text-base">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
            <p className="font-ui text-[10px] uppercase tracking-widest text-amber-300/40 mt-5 text-center">
              Updated {CONTENT_LAST_UPDATED} · Published {CONTENT_FIRST_PUBLISHED}
            </p>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
